import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import { skipToken } from '@reduxjs/toolkit/query';
import { useSnackbar } from 'notistack';

import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  PlayArrow as PlayArrowIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import {
  Button,
  Divider,
  Grid,
  Link as MuiLink,
  MenuItem,
  Paper,
  Stack,
  Typography,
  Box,
  Chip,
  Tab,
  Tabs,
  IconButton,
  TextField,
  Select,
} from '@mui/material';

import { TestModal } from './components';
import { InfoField, type InfoFieldChangeHandler } from '../../components';

import { RouterPaths } from '../../router/paths';
import { NotificationTemplateEventName, NotificationTemplateTransport } from '../../enums';
import { UpdateTemplateDTO, useGetTemplateQuery, useUpdateTemplateMutation } from '../../api';
import { permissionsCheck } from '../../utils/permission';
import { HeadCell, Row, RowCell } from '../../components/table/types';
import { UTCToDateTime } from '../../utils';
import { Table } from '../../components/table/table';
import Title from '../../components/title/Title';
import { Channels } from './components/Channels';

export const NotificationTemplate: FC = () => {
  const { t } = useTranslation();
  const { eventName } = useParams<{ eventName: `${NotificationTemplateEventName}` }>();
  const { enqueueSnackbar } = useSnackbar();
  const hasPermissionToEditNotificationTemplate = permissionsCheck([
    'WRITE_NOTIFICATION_TEMPLATES',
  ]);

  // api
  const {
    data: template,
    isLoading: isTemplateLoading,
    refetch,
  } = useGetTemplateQuery(eventName ? { eventName } : skipToken);

  const [updateTemplate, { isLoading: isUpdateTemplateLoading }] = useUpdateTemplateMutation();

  // variablesW
  const isLoading = isTemplateLoading || isUpdateTemplateLoading;

  // test modal logic
  const [testModalShown, setTestModalShown] = useState(false);
  const openTestModal = () => {
    setTestModalShown(true);
  };
  const closeTestModal = () => {
    setTestModalShown(false);
  };

  // fields control logic
  const [fields, setFields] = useState<Partial<UpdateTemplateDTO['body']> | null>(null);
  const fieldsHandler: InfoFieldChangeHandler = (e, notEvent, fieldName) => {
    // @ts-ignore
    const name = notEvent && fieldName ? fieldName : e.target.name;
    // @ts-ignore
    const value = notEvent ? e : e.target.value;

    setFields((prevState) => {
      if (!prevState) {
        return {
          [name]: value,
        };
      }

      return {
        ...prevState,
        [name]: value,
      };
    });
  };

  // validation for required fields
  const requiredFields = [
    'subject',
    'text',
    'html',
    'transport',
  ] as (keyof UpdateTemplateDTO['body'])[];
  const checkSubmitDisabling = () => {
    if (!fields) return true;

    for (const field of requiredFields) {
      if (fields[field] === undefined || fields[field] === '' || fields[field] === null) {
        return true;
      }
    }

    return false;
  };

  // edit mode logic
  const [isEditMode, setEditMode] = useState(false);
  const openEditMode = () => {
    template?.template &&
      setFields({
        ...template.template,
        transport: template.template.enabled_transports,
      });
    setEditMode(true);
  };
  const closeEditMode = () => {
    setEditMode(false);
    setFields(null);
  };

  // edit handler
  const editTemplate = async () => {
    if (!eventName || !template || !fields) return;

    const args = {
      eventName: eventName,
      body: {
        subject: fields.subject,
        text: fields.text,
        html: fields.html,
        transport: fields.transport,
      },
    };

    try {
      // @ts-ignore
      await updateTemplate(args).unwrap();
      closeEditMode();
      enqueueSnackbar(t('ui.snackbar.success'), { variant: 'success' });
    } catch (e) {
      // @ts-ignore
      enqueueSnackbar(e?.data?.message ?? t('ui.snackbar.error'), { variant: 'error' });
    }
  };

  const copySpecification = (text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      enqueueSnackbar(t('ui.snackbar.success-copy'), { variant: 'success' });
    });
  };

  // elements
  const transportsSelectOptions = Object.values(NotificationTemplateTransport).map((transport) => {
    return (
      <MenuItem key={transport} value={transport}>
        {transport}
      </MenuItem>
    );
  });

  return (
    <>
      <Stack direction="row" alignItems="center" spacing={2} pl={3} pt={1} width="100%">
        <MuiLink underline="hover" component={Link} to={RouterPaths.NotificationTemplates}>
          <Stack direction="row" alignItems="stretch" spacing={0.2}>
            <ArrowBackIcon fontSize="inherit" />
            <Typography variant="caption" component="span">
              {t('notification-template.action-btn.back-to-templates')}
            </Typography>
          </Stack>
        </MuiLink>

        <Divider orientation="vertical" />

        <Typography color="black">{eventName}</Typography>

        <Stack direction="row" justifyContent="flex-end" spacing={2} flexGrow={1}>
          {!isEditMode && hasPermissionToEditNotificationTemplate && (
            <Button
              aria-label="edit"
              variant="outlined"
              color="warning"
              startIcon={<EditIcon />}
              disabled={isLoading}
              onClick={() => openEditMode()}
            >
              {t('notification-template.action-btn.edit')}
            </Button>
          )}

          {isEditMode && (
            <>
              <Button
                variant="contained"
                onClick={() => editTemplate()}
                disabled={isLoading || checkSubmitDisabling()}
              >
                {t('notification-template.action-btn.save')}
              </Button>
              <Button variant="outlined" onClick={() => closeEditMode()} disabled={isLoading}>
                {t('notification-template.action-btn.cancel')}
              </Button>
            </>
          )}

          {!isEditMode && (
            <Button
              aria-label="test"
              variant="outlined"
              startIcon={<PlayArrowIcon />}
              disabled={isLoading}
              onClick={() => openTestModal()}
            >
              {t('notification-template.action-btn.test')}
            </Button>
          )}
        </Stack>
      </Stack>

      <Grid size={{ xs: 12 }} sx={{ '&.MuiGrid-item': { pt: 1.6 } }}>
        <Paper sx={{ p: '24px 16px', display: 'flex', flexDirection: 'column', gap: 1 }}>
          <InfoField
            name={t('notification-template.id')}
            value={template?.template.id}
            withEmptyValue
          />

          <InfoField
            name={t('notification-template.event-name')}
            value={template?.template.event_name}
            withEmptyValue
          />

          <Stack direction="row" spacing={2} alignItems={'initial'}>
            <Typography sx={{ width: '120px' }} color="darkslategray">
              {t('notification-template.specification')}
            </Typography>
            <Box sx={{ maxWidth: '360px', width: '100%' }}>
              {template?.template.specification &&
                template.template.specification.map((it, index) => (
                  <Chip
                    key={index}
                    label={`{{${it}}}`}
                    onClick={() => copySpecification(`{{${it}}}`)}
                  />
                ))}
            </Box>
          </Stack>

          <Divider />

          <InfoField
            name={t('notification-template.text')}
            value={template?.template.text}
            editProps={{ withEditMode: true, isEditMode }}
            inputProps={{
              name: 'text',
              value: fields?.text ?? '',
              onChange: fieldsHandler,
              componentType: 'textarea',
              mitTextAreaRows: 3,
            }}
            isLoading={isLoading}
            withEmptyValue
          />

          <InfoField
            name={t('notification-template.html')}
            value={template?.template.html}
            editProps={{ withEditMode: true, isEditMode }}
            inputProps={{
              name: 'html',
              value: fields?.html ?? '',
              onChange: fieldsHandler,
              componentType: 'textarea',
              mitTextAreaRows: 3,
            }}
            isLoading={isLoading}
            withEmptyValue
          />

          <InfoField
            name={t('notification-template.enabled-transports')}
            value={template?.template.enabled_transports}
            formattedValue={template?.template.enabled_transports.join(', ')}
            editProps={{ withEditMode: true, isEditMode }}
            inputProps={{
              name: 'transport',
              value: fields?.transport ?? [],
              onChange: fieldsHandler,
              componentType: 'multipleSelect',
              selectOptions: transportsSelectOptions,
            }}
            isLoading={isLoading}
            withEmptyValue
          />

          <InfoField
            name={t('notification-template.subject')}
            value={template?.template.subject}
            editProps={{ withEditMode: true, isEditMode }}
            inputProps={{
              name: 'subject',
              value: fields?.subject ?? '',
              onChange: fieldsHandler,
              componentType: 'textarea',
              mitTextAreaRows: 3,
            }}
            isLoading={isLoading}
            withEmptyValue
            withoutDivider
          />
        </Paper>
      </Grid>

      {template?.template.event_name.includes('ADMIN_') && (
        <Channels
          notification_template_channels={template?.template.notification_template_channels}
          refresh={refetch}
        />
      )}

      <TestModal
        //
        open={testModalShown}
        onClose={closeTestModal}
        template={template?.template}
      />
    </>
  );
};
